import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CreditCard, Package, AlertTriangle, Check, ExternalLink, ShoppingCart, ArrowUpRight, Calendar, Zap, Users, FileText, HardDrive, Link2, MessageSquare, Sparkles, Shield, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import apiClient from '@/services/api/client';
import { PageHeader } from '@/components/shared/PageHeader';

function fmtBytes(bytes: number) {
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(0)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
}

function fmtCurrency(v: number) { return v >= 0 ? `$${v.toLocaleString()}` : `-$${Math.abs(v).toLocaleString()}`; }

const PLAN_COLORS: Record<string, string> = {
  trial: 'from-zinc-500 to-zinc-600',
  starter: 'from-blue-500 to-blue-600',
  professional: 'from-indigo-500 to-indigo-600',
  enterprise: 'from-purple-500 to-purple-600',
  unlimited: 'from-amber-500 to-amber-600',
};

const PLAN_LABELS: Record<string, string> = {
  trial: 'Free Trial',
  starter: 'Starter',
  professional: 'Professional',
  enterprise: 'Enterprise',
  unlimited: 'Scale',
};

const METER_ICONS: Record<string, any> = {
  'AI Queries': MessageSquare,
  'Clients': Users,
  'Documents': FileText,
  'Storage': HardDrive,
  'Users': Users,
  'Xero Connections': Link2,
};

export default function BillingPage() {
  const [cancelOpen, setCancelOpen] = useState(false);
  const queryClient = useQueryClient();

  const cancelMutation = useMutation({
    mutationFn: async () => {
      await apiClient.post('/subscription/upgrade', { planName: 'trial' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing'] });
      toast.success('Subscription cancelled. You have been moved to the Free Trial plan.');
      setCancelOpen(false);
    },
    onError: () => toast.error('Failed to cancel subscription. Please contact support.'),
  });

  const { data, isLoading, error: fetchError } = useQuery({
    queryKey: ['billing'],
    queryFn: async () => { const r = await apiClient.get('/subscription/billing'); return r.data.data; },
  });

  const { data: marketplaceData } = useQuery({
    queryKey: ['marketplace-items'],
    queryFn: async () => { const r = await apiClient.get('/subscription/marketplace'); return r.data.data; },
  });

  const plan = data?.plan;
  const usage = data?.usage;
  const payments = data?.payments || [];
  const addons = data?.addons || [];
  const marketplace = (marketplaceData || []).filter((m: any) => m.category === 'feature');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Loading billing details...</p>
        </div>
      </div>
    );
  }

  if (data?.status === 'no_subscription' || fetchError) {
    return (
      <div className="space-y-6">
        <PageHeader
          icon={CreditCard}
          title="Billing & Subscription"
          description="Manage your plan, usage, and payments"
        />
        <Card>
          <CardContent className="pt-12 pb-12 text-center">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-5">
              <Package className="h-7 w-7 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No Active Subscription</h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
              Choose a plan to unlock AI-powered accounting intelligence for your firm.
            </p>
            <Button onClick={() => window.location.href = '/panel/upgrade'} size="lg">
              <Sparkles className="h-4 w-4 mr-2" />
              Choose a Plan
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const planName = plan?.planName || 'starter';
  const planLabel = PLAN_LABELS[planName] || planName;
  const planGradient = PLAN_COLORS[planName] || PLAN_COLORS.starter;

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        icon={CreditCard}
        title="Billing & Subscription"
        description="Manage your plan, usage, and payments"
      />


      {/* Plan Overview Card */}
      <Card className="overflow-hidden">
        <div className={cn('bg-gradient-to-r p-6 text-white', planGradient)}>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Shield className="h-5 w-5 opacity-80" />
                <p className="text-xs uppercase tracking-wider opacity-80">Current Plan</p>
              </div>
              <h2 className="text-2xl font-bold">{planLabel}</h2>
            </div>
            <Badge variant="secondary" className={cn(
              'text-xs font-medium',
              plan?.status === 'active' ? 'bg-white/20 text-white border-white/30' :
              plan?.status === 'trial' ? 'bg-white/20 text-white border-white/30' :
              'bg-red-500/20 text-red-100 border-red-300/30'
            )}>
              {plan?.status === 'active' ? 'Active' : plan?.status === 'trial' ? 'Trial' : plan?.status}
            </Badge>
          </div>
        </div>
        <CardContent className="pt-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {plan?.currentPeriodStart && (
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Billing Period</p>
                  <p className="text-sm font-medium">
                    {new Date(plan.currentPeriodStart).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                    {' - '}
                    {plan.currentPeriodEnd ? new Date(plan.currentPeriodEnd).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Ongoing'}
                  </p>
                </div>
              </div>
            )}
            {plan?.trialEndsAt && (
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Zap className="h-4 w-4 text-amber-500" />
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Trial Ends</p>
                  <p className="text-sm font-medium">{new Date(plan.trialEndsAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                </div>
              </div>
            )}
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center">
                <CreditCard className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Monthly Cost</p>
                <p className="text-sm font-medium">{plan?.price ? `$${parseFloat(plan.price).toFixed(0)}/mo` : 'Free'}</p>
              </div>
            </div>
          </div>
          <Separator className="my-4" />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <p className="text-xs text-muted-foreground">
                Need more features or higher limits?
              </p>
              {planName !== 'trial' && (
                <button
                  onClick={() => setCancelOpen(true)}
                  className="text-[11px] text-muted-foreground hover:text-destructive transition-colors"
                >
                  Cancel subscription
                </button>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={() => window.location.href = '/panel/upgrade'}>
              <ArrowUpRight className="h-3.5 w-3.5 mr-1.5" />
              Upgrade Plan
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Usage Meters */}
      {usage && (
        <>
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Usage This Period</h2>
            <p className="text-xs text-muted-foreground">Resets each billing cycle</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { label: 'AI Queries', ...usage.aiQueries, format: (v: number) => v.toLocaleString() },
              { label: 'Clients', ...usage.clients, format: (v: number) => v.toLocaleString() },
              { label: 'Documents', ...usage.documents, format: (v: number) => v.toLocaleString() },
              { label: 'Storage', ...usage.storage, format: fmtBytes },
              { label: 'Users', ...usage.users, format: (v: number) => v.toLocaleString() },
              { label: 'Xero Connections', ...usage.xero, format: (v: number) => v.toLocaleString() },
            ].map((meter) => {
              const Icon = METER_ICONS[meter.label] || Package;
              const isWarning = meter.percent >= 70 && meter.percent < 90;
              const isDanger = meter.percent >= 90;

              return (
                <Card key={meter.label} className={cn(isDanger && 'border-red-500/30', isWarning && 'border-yellow-500/30')}>
                  <CardContent className="pt-5 pb-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className={cn(
                          'w-8 h-8 rounded-lg flex items-center justify-center',
                          isDanger ? 'bg-red-500/10' : isWarning ? 'bg-yellow-500/10' : 'bg-muted'
                        )}>
                          <Icon className={cn(
                            'h-4 w-4',
                            isDanger ? 'text-red-500' : isWarning ? 'text-yellow-500' : 'text-muted-foreground'
                          )} />
                        </div>
                        <span className="text-sm font-medium">{meter.label}</span>
                      </div>
                      <span className={cn(
                        'text-xs font-mono',
                        isDanger ? 'text-red-500' : isWarning ? 'text-yellow-500' : 'text-muted-foreground'
                      )}>
                        {Math.round(meter.percent)}%
                      </span>
                    </div>
                    <Progress
                      value={Math.min(meter.percent, 100)}
                      className={cn('h-2', isDanger ? '[&>div]:bg-red-500' : isWarning ? '[&>div]:bg-yellow-500' : '')}
                    />
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[11px] text-muted-foreground">{meter.format(meter.used)} used</span>
                      <span className="text-[11px] text-muted-foreground">{meter.format(meter.max)} limit</span>
                    </div>
                    {isDanger && (
                      <div className="flex items-center gap-1.5 mt-2 text-red-500">
                        <AlertTriangle className="h-3 w-3" />
                        <p className="text-[10px] font-medium">Approaching limit. Consider upgrading.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {/* Active Add-ons */}
      {addons.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Package className="h-4 w-4" />
              Active Add-ons
            </CardTitle>
            <CardDescription className="text-xs">Additional features and quota packs on your account</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Add-on</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Type</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground">Price</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Active Since</th>
                </tr>
              </thead>
              <tbody>
                {addons.map((a: any, i: number) => (
                  <tr key={i} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-3 font-medium">{a.name}</td>
                    <td className="px-4 py-3"><Badge variant="outline" className="text-[10px]">{a.category}</Badge></td>
                    <td className="px-4 py-3 text-right font-mono">${parseFloat(a.price_monthly).toFixed(0)}/mo</td>
                    <td className="px-4 py-3 text-muted-foreground">{new Date(a.started_at).toLocaleDateString('en-AU')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Feature Marketplace */}
      {marketplace.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ShoppingCart className="h-4 w-4" />
              Feature Marketplace
            </CardTitle>
            <CardDescription className="text-xs">Purchase individual features as add-ons to your plan</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {marketplace.map((item: any) => {
                const isOwned = plan?.features?.includes(item.feature_key) || addons.some((a: any) => a.feature_key === item.feature_key);
                return (
                  <div key={item.id} className={cn(
                    'border rounded-lg p-4 transition-all duration-200',
                    isOwned ? 'border-green-500/30 bg-green-500/5' : 'border-border hover:border-primary/30 hover:bg-muted/30'
                  )}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold">{item.name}</span>
                      {isOwned ? (
                        <Badge className="text-[9px] bg-green-500/20 text-green-600 dark:text-green-400 border-none">
                          <Check className="h-3 w-3 mr-0.5" />Included
                        </Badge>
                      ) : (
                        <span className="text-sm font-bold text-primary">${parseFloat(item.price_monthly).toFixed(0)}/mo</span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{item.description}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Payment History
          </CardTitle>
          <CardDescription className="text-xs">Your recent transactions and invoices</CardDescription>
        </CardHeader>
        <CardContent>
          {payments.length === 0 ? (
            <div className="text-center py-8">
              <CreditCard className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No payments yet</p>
              <p className="text-xs text-muted-foreground mt-1">
                {plan?.status === 'trial' ? 'Your trial is free. Payments will appear after you upgrade.' : 'Payment records will appear here after your first billing cycle.'}
              </p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Date</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Description</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground">Amount</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Status</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground">Invoice</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p: any, i: number) => (
                  <tr key={i} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-3 text-muted-foreground">{new Date(p.created_at).toLocaleDateString('en-AU')}</td>
                    <td className="px-4 py-3">{p.description || 'Subscription payment'}</td>
                    <td className="px-4 py-3 text-right font-mono font-medium">{fmtCurrency(parseFloat(p.amount))}</td>
                    <td className="px-4 py-3">
                      <Badge variant={p.status === 'succeeded' ? 'default' : 'destructive'} className="text-[10px]">
                        {p.status === 'succeeded' ? 'Paid' : p.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {p.invoice_url && (
                        <a href={p.invoice_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1 text-xs">
                          View <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Help */}
      <div className="text-center pb-6">
        <p className="text-xs text-muted-foreground">
          Need help with billing?{' '}
          <a href="mailto:hello@ronidoc.ai" className="text-primary hover:underline">Contact support</a>
        </p>
      </div>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-destructive" />
              Cancel Subscription
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>Are you sure you want to cancel your <strong>{planLabel}</strong> plan?</p>
              <p>Your account will be downgraded to the Free Trial with limited access:</p>
              <ul className="text-sm list-disc list-inside space-y-1 mt-2">
                <li>3 clients maximum</li>
                <li>50 AI queries total</li>
                <li>No Xero connection</li>
                <li>Basic export only</li>
              </ul>
              <p className="text-xs text-muted-foreground mt-3">
                Your data will be preserved. You can upgrade again at any time.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep My Plan</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => cancelMutation.mutate()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending ? 'Cancelling...' : 'Yes, Cancel Subscription'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
